import { useState, useEffect } from "react";
import { GAME_STAGES, DEFAULT_ROOM } from "../../constants/gameStages";
import socket from "../../socket/socket";
import WaitingButton from "../../components/WaitingButton";
import { useRoleBackground } from "../../hooks/useRoleBackground";
import { useLanguage } from "../../context/LanguageContext";
import originalGraphic from "../../assets/original_graphic_b.png";
import otherGraphic from "../../assets/in_other_words_graphic_b.png";

function WaitingForGamemode({ role, gameStage }) {
  const [canProceed, setCanProceed] = useState(false);
  const destination = "/showgamemode";
  const [hoveredGamemode, setHoveredGamemode] = useState(null); // only local
  const [confirmedGamemode, setConfirmedGamemode] = useState(null); // synced via socket

  const [warning, setWarning] = useState("");
  const { bgStyle, className } = useRoleBackground("blue");
  const { t } = useLanguage();

  const [dotCount, setDotCount] = useState(1);

  const [opacityNormal, setopacityNormal] = useState(100);
  const [opacityOther, setopacityOther] = useState(100);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev === 3 ? 1 : prev + 1));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const dotString = ".".repeat(dotCount);

  // On mount, guesser requests the current mode only if not in READY state (not a new round)
  // In READY state (new round), wait for describer to actively select
  useEffect(() => {
    if (role === "guesser" && gameStage !== GAME_STAGES.READY) {
      console.log("🔍 Guesser requesting gamemode | gameStage:", gameStage);
      socket.emit("get_gamemode", { room: DEFAULT_ROOM });
    }
  }, [role, gameStage]);

  // Describer responds if mode already selected
  useEffect(() => {
    const handleGetGamemode = () => {
      if (role === "describer" && confirmedGamemode) {
        socket.emit("gamemode_selected", {
          mode: confirmedGamemode,
          room: DEFAULT_ROOM,
        });
      }
    };
    socket.on("get_gamemode", handleGetGamemode);
    return () => socket.off("get_gamemode", handleGetGamemode);
  }, [role, confirmedGamemode]);

  // Receive selected mode from describer
  useEffect(() => {
    const handleGamemodeSelected = (data) => {
      if (data?.mode) {
        setConfirmedGamemode(data.mode);
        setCanProceed(true);
        if (data.mode === "normal") {
          setopacityNormal(100);
          setopacityOther(50);
        } else {
          setopacityNormal(50);
          setopacityOther(100);
        }
      }
    };
    socket.on("gamemode_selected", handleGamemodeSelected);
    return () => socket.off("gamemode_selected", handleGamemodeSelected);
  }, []);

  // Reset mode when returning to WAITING (new game) or READY (new round)
  useEffect(() => {
    if (gameStage === GAME_STAGES.WAITING) {
      console.log("🔄 Resetting gamemode selection state for new game | gameStage:", gameStage);
      setConfirmedGamemode(null);
      setHoveredGamemode(null);
      setopacityNormal(100);
      setopacityOther(100);
      setCanProceed(false);
    } else if (gameStage === GAME_STAGES.READY) {
      console.log("🔄 Resetting canProceed for new round | gameStage:", gameStage);
      // Keep confirmedGamemode (mode persists across rounds), but reset canProceed
      // so guesser sees "selecting" state until describer clicks
      setCanProceed(false);
    }
  }, [gameStage]);

  // Reset canProceed when describer resets the game
  useEffect(() => {
    if (!confirmedGamemode) {
      setCanProceed(false);
    } else {
      setCanProceed(true);
    }
  }, [confirmedGamemode]);

  return (
    <div
      className={`flex flex-col justify-center items-center ${className}`}
      style={bgStyle}
    >
      <div className="text-center">
        {!canProceed ? (
          <h1 className="text-4xl font-bold text-black mt-[4px]">
            {t('guesser.selecting', 'The Describer is selecting a game mode')}
            <span className="text-4xl font-bold text-black inline-block w-12 text-left">{dotString}</span>
          </h1>
        ) : (
          <h1 className="text-4xl font-bold text-black mt-[4px]">
            {t('guesser.selected', 'The Describer has selected a game mode!')}
          </h1>
        )}

        <div className="grid grid-cols-2 gap-[24px] rounded-lg place-items-center items-stretch h-[490px] w-full px-[64px] my-[24px]">
          {/* Normal Mode */}
          <div
            className={`opacity-${opacityNormal} bg-white flex flex-col items-center w-full rounded-xl`}
          >
            <div className="mt-0 my-[23px] w-full h-full">
              <h2 className="bg-blue400 w-full rounded-t-lg font-bold text-white text-xl text-[22px] py-[16px]">
                {t('gamemode.mindOverMachine.title', 'MIND OVER MACHINE')}
              </h2>
            </div>
            <div>
              <img
                src={originalGraphic}
                alt="graphic icon"
                className="mt-[79px] w-[216px] h-[123px] object-cover rounded"
              />
            </div>
            <div className="mt-[80px] mx-[19px] mb-[24px]">
              <p className="text-black text-[20px] font-normal">
                {t('gamemode.mindOverMachine.description', 'Can you outwit AI? Give clever clues that guide your teammate but confuse the computer. Find the gaps in machine understanding and win with human smarts!')}
              </p>
            </div>
          </div>

          {/* In Other Words Mode */}
          <div
            className={`opacity-${opacityOther} bg-white flex flex-col items-center w-full  rounded-xl `}
          >
            <div className="mt-0 my-[23px] w-full h-full">
              <h2 className="bg-blue400 w-full rounded-t-lg font-bold text-white text-xl text-[22px] py-[16px]">
                {t('gamemode.inOtherWords.title', 'IN OTHER WORDS')}
              </h2>
            </div>
            <div>
              <img
                src={otherGraphic}
                alt="graphic icon"
                className="mt-[79px] w-[216px] h-[123px] object-cover rounded"
              />
            </div>
            <div className="mt-[80px] mx-[19px] mb-[24px]">
              <p className="text-black text-[20px] font-normal">
                {t('gamemode.inOtherWords.description', 'What can you do with a handful of random words? Make a sentence that sparks surprising connections and associations to throw off the AI.')}
              </p>
            </div>
          </div>
        </div>

        <div className="mb-[64px]">
          {canProceed ? (
            <WaitingButton
              proceed={true}
              destination={destination}
              gamemode={confirmedGamemode}
              nextMsg={t('button.continue', 'Continue!')}
              nextClassName="btn-blue-lg mt-[4px]"
              className="btn-blue-lg mt-[4px]"
            />
          ) : (
            <button className="btn-blue-lg mt-[4px]" disabled={true}>
              {t('button.continue', 'Continue!')}
            </button>
          )}
        </div>
        {warning && <div className="text-red-500 mb-2">{warning}</div>}
      </div>
    </div>
  );
}

export default WaitingForGamemode;
